import { AgentPageProps } from "@/types/agent/agent-page";

export interface StudioModeFlags {
  isEmbedMode: boolean;
  portalMode: boolean;
  demoMode: AgentPageProps["demoMode"];
  /** True when the widget popup should hand off to the early embed component,
   * before any of this page's own hooks/state matter. */
  isEarlyEmbedHandoff: boolean;
}

/**
 * Derives the page's operating mode from route props + the current URL —
 * portal/demo (passed in as props) and embed (query param or iframe
 * detection). Pure derivation, no state, no effects, so it's safe to call
 * unconditionally above every early return.
 */
export function useStudioModeFlags(
  portalMode: boolean,
  demoMode: AgentPageProps["demoMode"],
): StudioModeFlags {
  const searchParams = new URLSearchParams(window.location.search);

  const embedParam = searchParams.get("embed") === "true";
  const isInIframe = window.parent !== window;
  // ?layout=full keeps the standalone studio page inside a frame instead of
  // falling back to the compact widget layout — matches LiveAgentPage's
  // fullLayoutParam (Gulf Western's MAXX frames RobBot full-screen).
  const fullLayoutParam = searchParams.get("layout") === "full";
  const isEmbedMode = !fullLayoutParam && (embedParam || isInIframe);

  // See the EARLY EMBED HANDOFF note at the call site: this condition must be
  // computed unconditionally (no early return here) so hook order never
  // changes across renders of the same mounted instance.
  // `?embed=true` always means the popup widget, framed or opened directly in
  // a tab, so there is one embed look to debug rather than a second compact
  // Studio variant.
  const isEarlyEmbedHandoff =
    typeof window !== "undefined" &&
    !portalMode &&
    !demoMode &&
    embedParam;

  return { isEmbedMode, portalMode, demoMode, isEarlyEmbedHandoff };
}
